// What? A React component for managing data fields (input fields) in document type creation/editing.
// Why? To allow users to define what custom data should be collected during document generation.
// How? The component's logic flow:
// 1. Add: Allow users to add new data field definitions
// 2. Configure: Set field label, type (Text/Number/Date/Selection/FreeText), required flag
// 3. Validation: Configure validation rules for Text, Number, and Date fields
// 4. Options: Define selection options for Selection type fields
// 5. Remove: Support deletion of fields (with API cleanup if persisted)
// 6. Auto-generate: Automatically create field name from label

import React, { useCallback } from "react";
import {
  Box,
  Button,
  Checkbox,
  FormControl,
  FormHelperText,
  FormLabel,
  IconButton,
  Input,
  Option,
  Select,
  Stack,
  Tooltip,
  Typography,
} from "@mui/joy";
import DeleteIcon from "@mui/icons-material/Delete";
import DragIndicatorIcon from "@mui/icons-material/DragIndicator";
import {
  DragDropContext,
  Droppable,
  Draggable,
  DropResult,
} from "@hello-pangea/dnd";

import { DocumentTypeInput } from "../../../types/documentTypes";
import { useValidation } from "../../../contexts/ValidationContext";
import { useDeleteDocumentTypeInput } from "../../../hooks/useDocumentTypeInputs";
import FieldValidationControls from "../fields/FieldValidationControls";
import FieldOptionsControls from "../fields/FieldOptionsControls";

interface DataFieldsTabProps {
  fields: DocumentTypeInput[];
  documentTypeId?: number;
  workspace_id: string;
  onChange: (fields: DocumentTypeInput[], deletedIds?: number[]) => void;
  showValidation: boolean;
}

const DataFieldsTab: React.FC<DataFieldsTabProps> = ({
  fields,
  documentTypeId,
  workspace_id,
  onChange,
  showValidation,
}) => {
  const { deleteInputField } = useDeleteDocumentTypeInput(workspace_id);
  const { setShowValidation } = useValidation();

  const formatFieldName = (label: string): string => {
    return label
      .trim()
      .replace(/[^a-zA-Z0-9\s]/g, "") // Remove special characters
      .replace(/\s+/g, "_") // Replace spaces with underscores
      .toLowerCase(); // Convert to lowercase
  };

  const handleAddField = () => {
    setShowValidation(false);
    const newField: DocumentTypeInput = {
      Label: "",
      Name: "",
      InputType: "Text",
      IsRequired: false,
      OrderIndex: fields.length,
      ValidationRules: JSON.stringify({}),
      HelpText: "",
    };
    onChange([...fields, newField]);
  };

  const handleRemoveField = async (index: number) => {
    const field = fields[index];
    const updatedFields = fields.filter((_, i) => i !== index);

    if (field.InputFieldID && documentTypeId) {
      try {
        await deleteInputField(documentTypeId, field.InputFieldID);
      } catch (error) {
        console.error("Failed to delete field:", error);
        return; // Don't update state if delete failed
      }
    }

    onChange(updatedFields);
  };

  const handleFieldChange = (
    index: number,
    updates: Partial<DocumentTypeInput>,
  ) => {
    const updatedFields = [...fields];
    const currentField = updatedFields[index];

    // Reset validation when user starts typing or changes options
    if ("Label" in updates || "Options" in updates) {
      setShowValidation(false);
    }

    // Auto-generate Name from Label
    if ("Label" in updates && updates.Label) {
      updates.Name = formatFieldName(updates.Label);
    }

    // Only clear options when changing FROM Selection type
    if (
      "InputType" in updates &&
      updates.InputType !== "Selection" &&
      currentField.InputType === "Selection"
    ) {
      updates.Options = "";
    }

    // Reset validation rules when changing type
    if (
      "InputType" in updates &&
      updates.InputType !== currentField.InputType
    ) {
      updates.ValidationRules = JSON.stringify({});
    }

    updatedFields[index] = {
      ...currentField,
      ...updates,
      ...(currentField.InputFieldID && {
        InputFieldID: currentField.InputFieldID,
      }),
    };

    onChange(updatedFields);
  };

  const onDragEnd = useCallback(
    (result: DropResult) => {
      if (!result.destination) return;
      if (result.source.index === result.destination.index) return;

      const items = Array.from(fields);
      const [reorderedItem] = items.splice(result.source.index, 1);
      items.splice(result.destination.index, 0, reorderedItem);

      // Update OrderIndex for all items
      const updatedFields = items.map((field, index) => ({
        ...field,
        OrderIndex: index,
      }));

      onChange(updatedFields);
    },
    [fields, onChange],
  );

  return (
    <Stack spacing={3}>
      <Stack direction="row" justifyContent="space-between" alignItems="center">
        <Typography level="h3">Data Fields</Typography>
        <Button
          variant="solid"
          color="primary"
          onClick={() => {
            setShowValidation(false); // Reset validation when adding new field
            handleAddField();
          }}
          size="sm"
        >
          + Add Field
        </Button>
      </Stack>

      <DragDropContext onDragEnd={onDragEnd}>
        <Droppable droppableId="data-fields">
          {(provided) => (
            <Stack
              spacing={2}
              {...provided.droppableProps}
              ref={provided.innerRef}
            >
              {fields.map((field, index) => {
                // Validation logic for Display Label
                let labelError: string | undefined = undefined;
                const trimmed = field.Label?.trim();
                if (showValidation) {
                  if (!trimmed) labelError = "Label is required";
                  else if (trimmed.length < 3)
                    labelError = "Label must be at least 3 characters";
                  else if (trimmed.length > 100)
                    labelError = "Label must be less than 100 characters";
                }

                const draggableId = field.InputFieldID
                  ? String(field.InputFieldID)
                  : `new-${index}`;

                return (
                  <Draggable
                    key={draggableId}
                    draggableId={draggableId}
                    index={index}
                  >
                    {(provided, snapshot) => (
                      <Box
                        ref={provided.innerRef}
                        {...provided.draggableProps}
                        sx={{
                          p: 2,
                          border: "1px solid",
                          borderColor: snapshot.isDragging
                            ? "primary.400"
                            : "neutral.300",
                          borderRadius: "sm",
                          bgcolor: snapshot.isDragging
                            ? "background.level1"
                            : "background.surface",
                          transition: "all 0.2s ease",
                          "&:hover": {
                            borderColor: "neutral.400",
                            boxShadow: "sm",
                          },
                          ...provided.draggableProps.style,
                        }}
                      >
                        <Stack spacing={2}>
                          {/* Top row: Drag handle, Label, Type, Required checkbox */}
                          <Box
                            sx={{
                              display: "grid",
                              gridTemplateColumns: {
                                xs: "auto 1fr",
                                sm: "auto 1fr 1fr",
                                md: "auto minmax(0, 2fr) minmax(0, 1fr) auto",
                              },
                              gap: 2,
                              alignItems: "start",
                            }}
                          >
                            {/* Drag handle */}
                            <Box
                              {...provided.dragHandleProps}
                              sx={{
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "center",
                                cursor: "grab",
                                color: "text.tertiary",
                                mt: 3.5,
                                "&:hover": {
                                  color: "text.secondary",
                                },
                                "&:active": {
                                  cursor: "grabbing",
                                },
                              }}
                            >
                              <DragIndicatorIcon fontSize="small" />
                            </Box>

                            <FormControl required error={!!labelError}>
                              <FormLabel>Display Label</FormLabel>
                              <Input
                                value={field.Label}
                                onChange={(e) =>
                                  handleFieldChange(index, {
                                    Label: e.target.value,
                                  })
                                }
                                placeholder="e.g., Employee Name, Project Budget"
                                slotProps={{ input: { maxLength: 100 } }}
                                size="lg"
                              />
                              {labelError && (
                                <FormHelperText>{labelError}</FormHelperText>
                              )}
                            </FormControl>

                            <FormControl>
                              <FormLabel>Type</FormLabel>
                              <Select
                                value={field.InputType}
                                onChange={(_, newType) => {
                                  if (newType) {
                                    handleFieldChange(index, {
                                      InputType: newType,
                                      ValidationRules: "",
                                      ...(newType === "Selection"
                                        ? { Options: "" }
                                        : {}),
                                    });
                                  }
                                }}
                                size="lg"
                              >
                                <Option value="Text">Text</Option>
                                <Option value="Number">Number</Option>
                                <Option value="Date">Date</Option>
                                <Option value="Selection">Selection</Option>
                                <Option value="FreeText">Free Text</Option>
                              </Select>
                            </FormControl>

                            <FormControl
                              orientation="horizontal"
                              sx={{ alignSelf: "end", pb: 0.5 }}
                            >
                              <Checkbox
                                checked={field.IsRequired}
                                onChange={(e) =>
                                  handleFieldChange(index, {
                                    IsRequired: e.target.checked,
                                  })
                                }
                                label="Required"
                              />
                            </FormControl>
                          </Box>

                          {/* Options controls for Selection type */}
                          {field.InputType === "Selection" && (
                            <Box>
                              <FieldOptionsControls
                                fieldId={`field.${field.InputFieldID || index}.Options`}
                                options={field.Options?.split("|||") || []}
                                onChange={(newOptions) => {
                                  handleFieldChange(index, {
                                    Options: newOptions.join("|||"),
                                  });
                                }}
                              />
                            </Box>
                          )}

                          {/* Validation controls for Text, Number, Date */}
                          {["Text", "Number", "Date"].includes(
                            field.InputType,
                          ) && (
                            <Box>
                              <FieldValidationControls
                                type={field.InputType}
                                validation={
                                  field.ValidationRules
                                    ? JSON.parse(field.ValidationRules)
                                    : {}
                                }
                                onChange={(validation) =>
                                  handleFieldChange(index, {
                                    ValidationRules: JSON.stringify(validation),
                                  })
                                }
                              />
                            </Box>
                          )}

                          {/* Bottom row: Help text and delete button */}
                          <Stack
                            direction="row"
                            spacing={1}
                            alignItems="flex-start"
                          >
                            <FormControl sx={{ flex: 1 }}>
                              <FormLabel>Help Text</FormLabel>
                              <Input
                                value={field.HelpText || ""}
                                onChange={(e) =>
                                  handleFieldChange(index, {
                                    HelpText: e.target.value,
                                  })
                                }
                                placeholder="Optional guidance for users filling this field"
                                size="lg"
                              />
                            </FormControl>
                            <Tooltip title="Remove Field">
                              <IconButton
                                color="danger"
                                variant="plain"
                                onClick={() => handleRemoveField(index)}
                                sx={{ mt: 3.5 }}
                              >
                                <DeleteIcon />
                              </IconButton>
                            </Tooltip>
                          </Stack>
                        </Stack>
                      </Box>
                    )}
                  </Draggable>
                );
              })}
              {provided.placeholder}

              {(!fields || fields.length === 0) && (
                <Box sx={{ textAlign: "center", py: 4 }}>
                  <Typography level="body-md" color="neutral" sx={{ mb: 2 }}>
                    No data fields added yet.
                  </Typography>
                  <Typography level="body-sm" color="neutral">
                    Click "+ Add Field" to define custom data to collect during
                    document generation.
                  </Typography>
                </Box>
              )}
            </Stack>
          )}
        </Droppable>
      </DragDropContext>
    </Stack>
  );
};

export default DataFieldsTab;
